import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import toast from "react-hot-toast";
import { useNavigate, Link } from "react-router-dom";
import { fetchInsights, fetchInsightsChat, fetchInsightsScores, fetchPlatformChat, navigateToPortfolioPerformance, openPePortfolioReport } from "../api/analyticsClient";
import { executeAdminAction } from "../api/adminClient";
import {
  assignProfileBulk,
  createProfile,
  fetchAssignmentCompanies,
  fetchBlockCatalog,
  fetchLiteReportCompanies,
  openLiteCompanyReportPdf,
} from "../api/reportProfilesClient";
import { openReportDocument } from "./openReportDocument";
import NexusChatSidebar from "../ai/components/NexusChatSidebar";
import NexusConversationThread, { mapApiMessagesToThread } from "../ai/components/NexusConversationThread";
import { useNexusOptional } from "../ai/NexusProvider";
import { getVisibleCopilotChips, normalizeQuestion } from "./copilotSuggestions";
import ActionModePanel, { QUICK_ACTIONS } from "./ActionModePanel";
import { useWorkspaceProductDoc } from "./useWorkspaceProductDoc";
import { filterQuickActionsForWorkspace } from "./workspaceProductSettings";
import { IconSearch, IconSparkles, InsightResultCard, QuickActionTile } from "./assistantUi";
import WorkflowGuideCard from "./WorkflowGuideCard";
import {
  inferWorkflowGuideFromThread,
  inferWorkflowGuideId,
  isAffirmativeConfirm,
  isReminderConfirmAction,
  looksLikeInsightQuestion,
  looksLikeWorkflow,
  threadAwaitsReminderConfirm,
} from "./commandIntent";
import {
  buildWorkflowInstruction,
  buildWorkflowMessageFromChat,
  getWorkflowGuide,
  guideUsesCompanySelect,
  guideUsesMultiCompanySelect,
  guideUsesProfileCompanySelect,
  parseWorkflowGuideValuesFromText,
  parseCompaniesList,
  validateGuideValues,
} from "./workflowGuides";
import { defaultBlocksForBase, isTeamReportBase } from "./reportProfileDefaults";
import {
  ASSIGN_ALL_VALUE,
  ASSIGNMENT_SCOPES,
  assignmentPrefsToPlanPatch,
} from "./ProfileAssignmentForm";
import { LENS_ROUTES, ROUTES } from "./routes";
import {
  buildWorkflowAssistantThreadMessage,
} from "./workflowThreadMessages";
import { isAbortError } from "../utils/fetchAbort";

function isNavigationIntent(text) {
  return /\b(go to|open|navigate|take me to|show me the|bring me to|where is|where can i find)\b/i.test(
    text || ""
  );
}

function resolveNavigatePath(res, currentPath = "") {
  const structured = res?.data?.structured;
  const pages = structured?.related_pages || res?.data?.related_pages || [];
  const target = pages.find((p) => p?.path)?.path;
  if (!target) return null;

  const here = (currentPath || "").split("?")[0].replace(/\/$/, "") || "";
  const dest = target.split("?")[0].replace(/\/$/, "") || "";
  if (!dest) return null;
  if (dest === here) return null;
  if (dest === ROUTES.dashboard && here === ROUTES.dashboard) return null;

  return target;
}

function shouldAutoNavigate(question, res, currentPath) {
  const path = resolveNavigatePath(res, currentPath);
  if (!path) return null;
  if (isNavigationIntent(question)) return path;
  if (res?.data?.layer === "navigation" || res?.data?.route === "navigation") return path;
  return null;
}

const SHOW_SUGGESTED_CHIPS = false;

const CommandCenterPanel = forwardRef(function CommandCenterPanel(
  {
    context,
    pathname,
    disabled = false,
    showInsights = true,
    hideWorkflows = false,
    hideInput = false,
    variant = "default",
    placeholderOverride = "",
    usePlatformChat = false,
    initialPrompt = "",
    surfaceStrip = null,
    registerWorkflow = true,
    commandBarRefined = false,
  },
  ref
) {
  const isLanding = variant === "landing";
  const isDedicatedChat = variant === "chat";
  const deferToDedicatedChat = isLanding && usePlatformChat;
  const navigate = useNavigate();
  const nexus = useNexusOptional();
  const [command, setCommand] = useState("");
  const [localSessionId, setLocalSessionId] = useState("");
  const sessionId = usePlatformChat ? nexus?.platformSessionId || "" : localSessionId;
  const threadMessages = usePlatformChat ? nexus?.threadMessages || [] : [];
  const pendingUserMessage = usePlatformChat ? nexus?.pendingUserMessage || "" : "";
  const setThreadMessages = usePlatformChat ? nexus?.setThreadMessages : () => {};
  const setPendingUserMessage = usePlatformChat ? nexus?.setPendingUserMessage : () => {};
  const setSessionId = useCallback(
    (sid) => {
      const next = sid || "";
      if (usePlatformChat && nexus?.setPlatformSessionId) {
        nexus.setPlatformSessionId(next);
      } else {
        setLocalSessionId(next);
      }
    },
    [nexus, usePlatformChat]
  );
  const [lastQuery, setLastQuery] = useState("");
  const [answer, setAnswer] = useState("");
  const [relatedPages, setRelatedPages] = useState([]);
  const [dataSources, setDataSources] = useState([]);
  const [, setStructured] = useState(null);
  const [insightLoading, setInsightLoading] = useState(false);
  const [reminderSendBusy, setReminderSendBusy] = useState(false);
  const [askedQuestions, setAskedQuestions] = useState([]);
  const [pendingWorkflowId, setPendingWorkflowId] = useState(null);
  const [activeWorkflowGuideId, setActiveWorkflowGuideId] = useState(null);
  const [workflowFormId, setWorkflowFormId] = useState(null);
  const [guideValues, setGuideValues] = useState({});
  const [guideError, setGuideError] = useState("");
  const [workflowLoading, setWorkflowLoading] = useState(false);
  const [workflowPanelActive, setWorkflowPanelActive] = useState(false);
  const [reportCompanyOptions, setReportCompanyOptions] = useState([]);
  const [companiesLoading, setCompaniesLoading] = useState(false);
  const [blockCatalog, setBlockCatalog] = useState([]);
  const [blocksCatalogLoading, setBlocksCatalogLoading] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const showConversationHistory = isLanding;
  const workflowRef = useRef(null);
  const commandInputRef = useRef(null);
  const initialPromptHandled = useRef("");

  const workflowUiActive = !!(pendingWorkflowId || workflowFormId || workflowPanelActive || workflowLoading);
  const workspaceProduct = useWorkspaceProductDoc();
  const quickActions = useMemo(
    () => filterQuickActionsForWorkspace(QUICK_ACTIONS, workspaceProduct),
    [workspaceProduct]
  );

  useEffect(() => {
    if (
      !workflowFormId ||
      (!guideUsesCompanySelect(workflowFormId) &&
        !guideUsesMultiCompanySelect(workflowFormId) &&
        !guideUsesProfileCompanySelect(workflowFormId))
    ) {
      setReportCompanyOptions([]);
      setCompaniesLoading(false);
      return undefined;
    }

    let cancelled = false;
    setCompaniesLoading(true);

    const loadLiteCompanies = () =>
      fetchLiteReportCompanies()
        .then((res) => {
          if (cancelled) return;
          const names = Array.isArray(res?.data?.companies) ? res.data.companies : [];
          setReportCompanyOptions(names);
        })
        .catch(() => {
          if (!cancelled) setReportCompanyOptions([]);
        });

    const loadReportReadyCompanies = () =>
      Promise.all([
        fetchInsights({ includeScores: false }),
        fetchInsightsScores(),
      ])
        .then(([payload, scoresRes]) => {
          if (cancelled) return;
          const byId = scoresRes?.data?.scoresByOrgId || {};
          const orgs = payload?.data?.organizations;
          const names = Array.isArray(orgs)
            ? orgs
                .filter((o) => byId[o.id]?.reportReady && (o?.name || "").trim())
                .map((o) => String(o.name).trim())
                .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }))
            : [];
          setReportCompanyOptions(names);
        })
        .catch(() => {
          if (!cancelled) setReportCompanyOptions([]);
        });

    const loadAssignmentCompanies = () =>
      fetchAssignmentCompanies()
        .then((res) => {
          if (cancelled) return;
          const names = (res?.data?.companies || [])
            .map((c) => String(c).trim())
            .filter(Boolean)
            .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }));
          setReportCompanyOptions(names);
        })
        .catch(() => {
          if (!cancelled) setReportCompanyOptions([]);
        });

    const loader =
      workflowFormId === "lite"
        ? loadLiteCompanies()
        : workflowFormId === "profile"
          ? loadAssignmentCompanies()
          : loadReportReadyCompanies();
    loader.finally(() => {
      if (!cancelled) setCompaniesLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, [workflowFormId]);

  useEffect(() => {
    if (workflowFormId !== "profile") {
      setBlockCatalog([]);
      setBlocksCatalogLoading(false);
      return undefined;
    }

    let cancelled = false;
    setBlocksCatalogLoading(true);
    fetchBlockCatalog()
      .then((res) => {
        if (!cancelled) setBlockCatalog(Array.isArray(res?.data?.blocks) ? res.data.blocks : []);
      })
      .catch(() => {
        if (!cancelled) setBlockCatalog([]);
      })
      .finally(() => {
        if (!cancelled) setBlocksCatalogLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [workflowFormId]);

  const pendingGuide = pendingWorkflowId ? getWorkflowGuide(pendingWorkflowId) : null;

  const { chips: visibleChips, label: chipsLabel } = useMemo(
    () =>
      getVisibleCopilotChips({
        pathname,
        askedQuestions,
        lastQuestion: lastQuery,
        lastAnswer: answer,
      }),
    [pathname, askedQuestions, lastQuery, answer]
  );

  const hasInsightResult =
    usePlatformChat
      ? !!(threadMessages.length || pendingUserMessage || insightLoading || workflowUiActive)
      : !!(lastQuery || answer || insightLoading) && !workflowUiActive;
  const inChatMode = usePlatformChat && hasInsightResult && !isLanding;
  const showInlineConversation = inChatMode || isDedicatedChat;
  const commandBusy = insightLoading || workflowLoading;
  const workflowAwaitingInput = !!pendingWorkflowId;

  const showWorkflowBanner = pendingGuide && !workflowFormId;

  const canRun =
    !disabled && command.trim().length > 0 && !commandBusy && !workflowFormId;

  const inputPlaceholder = workflowPanelActive
    ? "Reply to continue the workflow (company name, role, etc.)…"
    : pendingGuide
      ? pendingGuide.inputPlaceholder
      : placeholderOverride
        ? placeholderOverride
        : showInsights
          ? inChatMode
            ? "Continue the conversation…"
            : "Ask about the whole portfolio or this page…"
          : "Describe a workflow in plain English…";

  const markAsked = (text) => {
    const key = normalizeQuestion(text);
    if (!key) return;
    setAskedQuestions((prev) => {
      if (prev.some((q) => normalizeQuestion(q) === key)) return prev;
      return [...prev, text.trim()];
    });
  };

  const stripWorkflowThreadMessages = useCallback(() => {
    if (!usePlatformChat) return;
    setThreadMessages((prev) => prev.filter((m) => m.meta?.kind !== "workflow_start"));
  }, [usePlatformChat, setThreadMessages]);

  const appendWorkflowThreadPrompt = useCallback(
    (guideId, guide) => {
      if (!usePlatformChat || !guide) return;
      const msg = {
        id: `workflow-start-${guideId}`,
        role: "assistant",
        content: guide.askPrompt,
        meta: {
          kind: "workflow_start",
          workflowId: guideId,
          workflowTitle: guide.title,
        },
      };
      setThreadMessages((prev) => {
        const without = prev.filter((m) => m.meta?.kind !== "workflow_start");
        return [...without, msg];
      });
    },
    [usePlatformChat, setThreadMessages]
  );

  const appendUserThreadMessage = useCallback(
    (text) => {
      if (!usePlatformChat) return;
      const trimmed = String(text || "").trim();
      if (!trimmed) return;
      setThreadMessages((prev) => [
        ...prev,
        { id: `user-${Date.now()}`, role: "user", content: trimmed },
      ]);
    },
    [usePlatformChat, setThreadMessages]
  );

  const appendWorkflowPlanToThread = useCallback(
    (data) => {
      if (!usePlatformChat || !data) return;
      const msg = buildWorkflowAssistantThreadMessage(data);
      if (!msg) return;
      setThreadMessages((prev) => [...prev, msg]);
    },
    [usePlatformChat, setThreadMessages]
  );

  const historySessions = useMemo(() => {
    const list = nexus?.conversationList || [];
    if (sessionId) return list;
    if (!threadMessages.length) return list;
    const firstUser = threadMessages.find((m) => m.role === "user");
    const preview = (firstUser?.content || "Current conversation").trim();
    return [
      {
        session_id: "__current__",
        title: preview.length > 56 ? `${preview.slice(0, 53)}…` : preview,
        preview,
        summary: preview,
        message_count: threadMessages.length,
        updated_at: new Date().toISOString(),
      },
      ...list,
    ];
  }, [nexus?.conversationList, sessionId, threadMessages]);

  const openHistory = useCallback(() => {
    setHistoryOpen(true);
    nexus?.refreshConversationList?.();
  }, [nexus]);

  useEffect(() => {
    if (!showConversationHistory) setHistoryOpen(false);
  }, [showConversationHistory]);

  const resetWorkflowState = useCallback(() => {
    setPendingWorkflowId(null);
    setActiveWorkflowGuideId(null);
    setWorkflowFormId(null);
    setGuideValues({});
    setGuideError("");
    workflowRef.current?.clearFlowHint?.();
    stripWorkflowThreadMessages();
  }, [stripWorkflowThreadMessages]);

  const pathnameRef = useRef(pathname);
  useEffect(() => {
    if (pathnameRef.current !== null && pathnameRef.current !== pathname) {
      resetWorkflowState();
      workflowRef.current?.resetWorkflow?.();
    }
    pathnameRef.current = pathname;
  }, [pathname, resetWorkflowState]);

  const startNewConversation = () => {
    if (usePlatformChat && nexus?.startNewChat) {
      nexus.startNewChat();
    } else {
      setSessionId("");
    }
    setHistoryOpen(false);
    setLastQuery("");
    setAnswer("");
    setStructured(null);
    setRelatedPages([]);
    setDataSources([]);
    setAskedQuestions([]);
    resetWorkflowState();
    workflowRef.current?.resetWorkflow?.();
  };

  const handleSelectConversation = async (sid) => {
    if (sid === "__current__") {
      setHistoryOpen(false);
      return;
    }
    if (!usePlatformChat || !nexus?.openConversation) return;
    const result = await nexus.openConversation(sid);
    if (!result?.ok) {
      toast.error("Could not load that conversation");
      return;
    }
    setHistoryOpen(false);
    const msgs = result.messages || [];
    const lastAssistant = [...msgs].reverse().find((m) => m.role === "assistant");
    const lastUser = [...msgs].reverse().find((m) => m.role === "user");
    setLastQuery(lastUser?.content || "");
    setAnswer(lastAssistant?.content || "");
    setStructured(lastAssistant?.structured || null);
    window.setTimeout(() => commandInputRef.current?.focus(), 0);
  };

  const runInsight = async (text) => {
    const q = text.trim();
    if (!q || disabled || insightLoading) return;
    setInsightLoading(true);
    setLastQuery(q);
    if (usePlatformChat) {
      setPendingUserMessage(q);
    } else {
      setAnswer("");
      setRelatedPages([]);
      setDataSources([]);
      setStructured(null);
    }
    markAsked(q);
    try {
      const res = usePlatformChat
        ? await fetchPlatformChat({
            message: q,
            hints: {
              surface: context?.surface || "command_center",
              currentRoute: context?.currentRoute || pathname || ROUTES.dashboard,
            },
            context,
            sessionId: sessionId || null,
          })
        : await fetchInsightsChat({
            message: q,
            context,
            sessionId: sessionId || null,
          });
      const sid = res?.data?.session_id || "";
      if (sid) setSessionId(sid);
      const reply = res?.data?.reply || "";
      setAnswer(reply);
      setStructured(res?.data?.structured || null);
      setRelatedPages(Array.isArray(res?.data?.related_pages) ? res.data.related_pages : []);
      setDataSources(Array.isArray(res?.data?.data_sources) ? res.data.data_sources : []);
      if (usePlatformChat) {
        const msgs = mapApiMessagesToThread(res?.data?.messages || []);
        const lastAssistant = [...msgs].reverse().find((m) => m.role === "assistant");
        if (lastAssistant) {
          setStructured(lastAssistant.structured || res?.data?.structured || null);
          setRelatedPages(res?.data?.related_pages || lastAssistant.relatedPages || []);
          setDataSources(res?.data?.data_sources || lastAssistant.dataSources || []);
        }
        setThreadMessages(msgs);
        setPendingUserMessage("");
        nexus?.bumpConversations?.();

        if (isLanding) {
          window.setTimeout(() => {
            document.getElementById("dashboard-command-section")?.scrollIntoView({
              behavior: "smooth",
              block: "center",
            });
          }, 80);
        }

        const navPath = shouldAutoNavigate(q, res, pathname || ROUTES.dashboard);
        if (navPath) {
          const label =
            res?.data?.related_pages?.[0]?.label ||
            res?.data?.structured?.related_pages?.[0]?.label ||
            navPath.split("/").pop()?.replace(/-/g, " ") ||
            "page";
          toast.success(`Opening ${label}…`);
          document.querySelector(".nexus-chat-thread-wrap")?.scrollIntoView?.({
            behavior: "smooth",
            block: "nearest",
          });
          window.setTimeout(() => navigate(navPath), 1600);
        }
      }
    } catch (err) {
      if (!isAbortError(err)) {
        toast.error(err?.message || "Could not run insight query");
      }
      setLastQuery("");
      setPendingUserMessage("");
      setAskedQuestions((prev) => prev.filter((item) => normalizeQuestion(item) !== normalizeQuestion(q)));
    } finally {
      setInsightLoading(false);
      if (usePlatformChat) {
        window.setTimeout(() => commandInputRef.current?.focus(), 0);
      }
    }
  };

  useEffect(() => {
    const prompt = (initialPrompt || "").trim();
    if (!prompt || disabled || initialPromptHandled.current === prompt) return;
    initialPromptHandled.current = prompt;
    runInsight(prompt);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialPrompt, disabled]);

  const ensureWorkflowGuide = useCallback(
    (guideId) => {
      if (!guideId) return;
      setActiveWorkflowGuideId(guideId);
      setPendingWorkflowId(guideId);
      const guide = getWorkflowGuide(guideId);
      if (guide && !threadMessages.some((m) => m.meta?.kind === "workflow_start" && m.meta?.workflowId === guideId)) {
        appendWorkflowThreadPrompt(guideId, guide);
      }
    },
    [appendWorkflowThreadPrompt, threadMessages]
  );

  const runWorkflow = async (text, { guideId: guideOverride } = {}) => {
    const trimmed = text.trim();
    if (!trimmed || disabled) return;
    const guideId =
      guideOverride || pendingWorkflowId || activeWorkflowGuideId || workflowFormId || inferWorkflowGuideId(trimmed);
    if (guideId) {
      ensureWorkflowGuide(guideId);
    }
    const awaitingConfirm = workflowRef.current?.isAwaitingWorkflowConfirm?.() === true;
    const message =
      guideId && !awaitingConfirm ? buildWorkflowMessageFromChat(guideId, trimmed) : trimmed;
    appendUserThreadMessage(trimmed);
    setWorkflowLoading(true);
    try {
      await workflowRef.current?.submitWithMessage?.(message);
      setCommand("");
      setWorkflowFormId(null);
      setGuideValues({});
      setGuideError("");
    } finally {
      setWorkflowLoading(false);
    }
  };

  const launchDedicatedChat = useCallback(
    (handoff) => {
      nexus?.startNewChat?.();
      nexus?.bumpConversations?.();
      nexus?.setChatHandoff?.(handoff);
      navigate(ROUTES.nexusChat);
    },
    [navigate, nexus]
  );

  const handlePrimarySubmit = async () => {
    const text = command.trim();
    if (!text || disabled) return;

    if (
      usePlatformChat &&
      isAffirmativeConfirm(text) &&
      threadAwaitsReminderConfirm(threadMessages)
    ) {
      setCommand("");
      resetWorkflowState();
      workflowRef.current?.resetWorkflow?.();
      await runInsight(text);
      return;
    }

    const workflowInProgress =
      workflowUiActive || workflowRef.current?.isWorkflowEngaged?.() === true;

    if (workflowInProgress) {
      setCommand("");
      if (deferToDedicatedChat) {
        launchDedicatedChat({ mode: "workflow", guideId: pendingWorkflowId || activeWorkflowGuideId, message: text });
        return;
      }
      await runWorkflow(text);
      return;
    }

    if (deferToDedicatedChat) {
      setCommand("");
      if (looksLikeWorkflow(text) && !looksLikeInsightQuestion(text)) {
        const guideId = inferWorkflowGuideId(text);
        if (guideId) {
          launchDedicatedChat({ mode: "workflow", guideId, message: text });
          return;
        }
      }
      launchDedicatedChat({ mode: "insight", message: text });
      return;
    }

    if (looksLikeWorkflow(text) && !looksLikeInsightQuestion(text)) {
      const guideId = inferWorkflowGuideId(text);
      if (guideId) {
        setCommand("");
        if (!pendingWorkflowId && !workflowFormId && !activeWorkflowGuideId) {
          startWorkflowTextGuide(guideId);
        }
        await runWorkflow(text, { guideId });
        return;
      }
    }

    if (usePlatformChat && !pendingWorkflowId && !activeWorkflowGuideId) {
      setCommand("");
      await runInsight(text);
      return;
    }

    if (looksLikeInsightQuestion(text)) {
      setCommand("");
      setPendingWorkflowId(null);
      await runInsight(text);
      return;
    }

    if (pendingWorkflowId || looksLikeWorkflow(text) || !showInsights) {
      setCommand("");
      await runWorkflow(text);
      return;
    }

    setCommand("");
    await runInsight(text);
  };

  const cancelWorkflow = () => {
    resetWorkflowState();
    workflowRef.current?.resetWorkflow?.();
  };

  const handleWorkflowActivityChange = (active) => {
    setWorkflowPanelActive(active);
  };

  const handleWorkflowComplete = useCallback(
    ({ message, reset = true } = {}) => {
      const text = String(message || "").trim();
      if (usePlatformChat && text) {
        setThreadMessages((prev) => [
          ...prev,
          {
            id: `workflow-done-${Date.now()}`,
            role: "assistant",
            content: text,
            meta: { kind: reset ? "workflow_complete" : "workflow_notice" },
          },
        ]);
      }
      if (reset) {
        resetWorkflowState();
        workflowRef.current?.resetWorkflow?.();
      }
    },
    [usePlatformChat, setThreadMessages, resetWorkflowState]
  );

  const startWorkflowTextGuide = useCallback(
    (guideId) => {
      if (disabled || commandBusy || !guideId) return;
      const guide = getWorkflowGuide(guideId);
      if (!guide) return;
      setActiveWorkflowGuideId(guideId);
      setPendingWorkflowId(guideId);
      setWorkflowFormId(null);
      setGuideValues({});
      setGuideError("");
      if (!threadMessages.some((m) => m.meta?.kind === "workflow_start" && m.meta?.workflowId === guideId)) {
        appendWorkflowThreadPrompt(guideId, guide);
      }
      if (guideId !== "invite") {
        workflowRef.current?.resetWorkflow?.();
      } else {
        workflowRef.current?.clearFlowHint?.();
      }
      if (isLanding) {
        window.setTimeout(() => {
          document.getElementById("dashboard-command-section")?.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
        }, 60);
      }
    },
    [appendWorkflowThreadPrompt, commandBusy, disabled, isLanding, threadMessages]
  );

  const startWorkflowChat = useCallback(
    (guideId) => {
      startWorkflowTextGuide(guideId);
      window.setTimeout(() => commandInputRef.current?.focus(), 0);
    },
    [startWorkflowTextGuide]
  );

  useEffect(() => {
    if (!registerWorkflow || !nexus?.registerWorkflowStart) return undefined;
    return nexus.registerWorkflowStart(startWorkflowChat);
  }, [nexus, registerWorkflow, startWorkflowChat]);

  useEffect(() => {
    const id = nexus?.pendingWorkflowRequest;
    if (!id) return;
    startWorkflowChat(id);
    nexus?.clearPendingWorkflowRequest?.();
  }, [nexus?.pendingWorkflowRequest, nexus?.clearPendingWorkflowRequest, startWorkflowChat]);

  const openWorkflowForm = () => {
    if (!pendingWorkflowId) return;
    const id = pendingWorkflowId;
    const lastUser = [...(threadMessages || [])].reverse().find((m) => m.role === "user");
    setWorkflowFormId(id);
    setPendingWorkflowId(null);
    setActiveWorkflowGuideId(id);
    setGuideValues(parseWorkflowGuideValuesFromText(id, lastUser?.content || ""));
    setGuideError("");
  };

  const updateGuideField = (key, value) => {
    setGuideValues((prev) => {
      const next = { ...prev, [key]: value };
      if (workflowFormId === "profile" && key === "reportBase") {
        next.blocks = defaultBlocksForBase(value);
      }
      return next;
    });
    setGuideError("");
  };

  useImperativeHandle(
    ref,
    () => ({
      startWorkflowChat,
      runInsight,
      runWorkflow,
      startNewConversation,
      focusInput: () => commandInputRef.current?.focus(),
      isBusy: () => commandBusy,
    }),
    [commandBusy, disabled, insightLoading, workflowLoading, pendingWorkflowId, startNewConversation, runWorkflow, runInsight, startWorkflowChat]
  );

  const submitWorkflowForm = async () => {
    if (!workflowFormId || disabled) return;
    const validation = validateGuideValues(workflowFormId, guideValues);
    if (!validation.ok) {
      setGuideError(validation.error);
      return;
    }
    if (guideUsesCompanySelect(workflowFormId) && !guideValues.company?.trim()) {
      setGuideError("Select a company from the list");
      return;
    }
    if (
      (guideUsesCompanySelect(workflowFormId) || guideUsesMultiCompanySelect(workflowFormId)) &&
      !reportCompanyOptions.length &&
      !companiesLoading
    ) {
      setGuideError(
        workflowFormId === "lite"
          ? "No companies with a completed LITE assessment are available yet"
          : "No report-ready companies are available yet"
      );
      return;
    }

    if (workflowFormId === "lite") {
      setWorkflowLoading(true);
      setGuideError("");
      try {
        await openLiteCompanyReportPdf(guideValues.company.trim());
        toast.success("Lite report opened");
        cancelWorkflow();
      } catch (err) {
        setGuideError(err?.message || "Could not open lite report");
      } finally {
        setWorkflowLoading(false);
      }
      return;
    }

    if (workflowFormId === "pe") {
      setWorkflowLoading(true);
      setGuideError("");
      try {
        const companies = parseCompaniesList(guideValues.companies);
        openPePortfolioReport(companies);
        toast.success("Generating portfolio report with charts…");
        cancelWorkflow();
      } catch (err) {
        setGuideError(err?.message || "Could not download portfolio report");
      } finally {
        setWorkflowLoading(false);
      }
      return;
    }

    if (workflowFormId === "profile") {
      setWorkflowLoading(true);
      setGuideError("");
      try {
        const reportBase = String(guideValues.reportBase || "company").trim();
        const isTeamBase = isTeamReportBase(reportBase);
        const payload = {
          name: String(guideValues.name || "").trim(),
          description: String(guideValues.description || "").trim(),
          target_role: String(guideValues.targetRole || "").trim() || null,
          output_format: "pdf",
          blocks: Array.isArray(guideValues.blocks) ? guideValues.blocks : [],
          is_company_base: !isTeamBase,
          is_team_base: isTeamBase,
        };
        const res = await createProfile(payload);
        const savedProfile = res?.data?.profile || null;
        const profileId = savedProfile?.profile_id || savedProfile?._id;
        if (profileId) {
          const companyValue = String(guideValues.company || ASSIGN_ALL_VALUE).trim();
          const assignmentPrefs =
            companyValue && companyValue !== ASSIGN_ALL_VALUE
              ? { companyScope: ASSIGNMENT_SCOPES.ONE, companyName: companyValue }
              : { companyScope: ASSIGNMENT_SCOPES.ALL, companyName: "" };
          const patch = assignmentPrefsToPlanPatch(assignmentPrefs, isTeamBase);
          try {
            const assignRes = await assignProfileBulk({
              profile_id: profileId,
              all_companies: patch.assign_to_all_companies,
              org_ids: patch.assign_to_company ? [patch.assign_to_company] : [],
              assign_as: patch.assign_as,
            });
            const count = assignRes?.data?.count || 0;
            toast.success(
              count
                ? `${isTeamBase ? "Team" : "Company"} profile created and assigned to ${count} company${count === 1 ? "" : "ies"}`
                : `${isTeamBase ? "Team" : "Company"} profile created`
            );
          } catch {
            toast.success(`${isTeamBase ? "Team" : "Company"} profile created (assignment pending)`);
          }
        } else {
          toast.success(`${isTeamBase ? "Team" : "Company"} report profile created`);
        }
        cancelWorkflow();
      } catch (err) {
        setGuideError(err?.message || "Could not create report profile");
      } finally {
        setWorkflowLoading(false);
      }
      return;
    }

    const instruction = buildWorkflowInstruction(workflowFormId, guideValues);
    if (!instruction) {
      setGuideError("Could not build instruction — check your entries");
      return;
    }
    await runWorkflow(instruction);
  };

  const submitPeOpenInsights = () => {
    if (!workflowFormId || workflowFormId !== "pe" || disabled) return;
    const validation = validateGuideValues(workflowFormId, guideValues);
    if (!validation.ok) {
      setGuideError(validation.error);
      return;
    }
    if (!reportCompanyOptions.length && !companiesLoading) {
      setGuideError("No report-ready companies are available yet");
      return;
    }
    try {
      const companies = parseCompaniesList(guideValues.companies);
      navigateToPortfolioPerformance(companies, navigate, { autoExport: false });
      toast.success("Opening Insights compare view…");
      cancelWorkflow();
    } catch (err) {
      setGuideError(err?.message || "Could not open Insights");
    }
  };

  const handleConfirmReminders = useCallback(
    async ({ invite_ids: inviteIds, flow_id: flowId }) => {
      const ids = (inviteIds || []).filter(Boolean);
      if (!ids.length || reminderSendBusy) return;
      setReminderSendBusy(true);
      try {
        const res = await executeAdminAction({
          plan: { invite_ids: ids },
          flow_id: flowId || undefined,
        });
        const msg = res?.message || `Sent ${ids.length} reminder email(s).`;
        toast.success(msg);
        setThreadMessages((prev) => [
          ...prev,
          {
            id: `reminder-sent-${Date.now()}`,
            role: "assistant",
            content: msg,
            structured: {
              summary: msg,
              sections: [],
              artifacts: [],
              actions: [],
              citations: [],
              related_pages: [],
              confidence: "high",
              answer_kind: "workflow",
            },
          },
        ]);
        resetWorkflowState();
        workflowRef.current?.resetWorkflow?.();
      } catch (err) {
        toast.error(err?.message || "Could not send reminders");
      } finally {
        setReminderSendBusy(false);
      }
    },
    [reminderSendBusy, resetWorkflowState]
  );

  const handleClarify = async (value, action) => {
    const text = (String(value || action?.label || "")).trim();
    if (!text || commandBusy) return;

    const actionType = action?.type || "";

    if (actionType === "confirm_reminder_send") {
      const ids = action?.invite_ids || action?.payload?.invite_ids;
      await handleConfirmReminders({
        invite_ids: ids,
        flow_id: action?.flow_id || action?.payload?.flow_id,
      });
      return;
    }

    if (actionType === "confirm" && (value === "open_report" || action?.value === "open_report")) {
      const doc = action?.document || action?.payload?.document;
      if (doc?.company_name) {
        try {
          const openResult = await openReportDocument(doc);
          const label = doc.label || "report";
          if (openResult?.downloaded) {
            toast.success("Report downloaded");
          } else {
            toast.success(`Opened ${label}`);
          }
        } catch (err) {
          toast.error(err?.message || "Could not open report");
        }
        return;
      }
      workflowRef.current?.runOpenReport?.();
      return;
    }

    if (
      actionType === "confirm" &&
      (isReminderConfirmAction(action) || threadAwaitsReminderConfirm(threadMessages))
    ) {
      if (usePlatformChat) {
        resetWorkflowState();
        workflowRef.current?.resetWorkflow?.();
        await runInsight("confirm");
        return;
      }
      runWorkflow("confirm", { guideId: "reminder" });
      return;
    }

    const workflowInProgress =
      workflowUiActive ||
      workflowPanelActive ||
      workflowRef.current?.isWorkflowEngaged?.() === true;

    const threadGuideId = inferWorkflowGuideFromThread(threadMessages);
    const reportGuideId =
      pendingWorkflowId || activeWorkflowGuideId || threadGuideId || null;

    if (
      workflowInProgress ||
      actionType === "select_role" ||
      (actionType === "select_company" && reportGuideId) ||
      actionType === "confirm" ||
      activeWorkflowGuideId
    ) {
      runWorkflow(actionType === "confirm" ? "confirm" : text, {
        guideId: reportGuideId || undefined,
      });
      return;
    }

    if (actionType === "select_company" || actionType === "clarify") {
      runInsight(text);
      return;
    }

    runInsight(text);
  };

  const selectedWorkflowId = pendingWorkflowId || activeWorkflowGuideId;

  const workflowsStrip =
    !hideWorkflows ? (
      <div
        className={`nexus-workflows-strip shrink-0 ${
          inChatMode ? "nexus-workflows-strip--in-chat" : ""
        }`}
      >
        <p className="nexus-workflows-strip-label text-[11px] font-semibold uppercase tracking-wider mb-2">
          {inChatMode ? "Workflows" : "Workflows"}
        </p>
        <div className="flex flex-nowrap gap-2 sm:gap-3 overflow-x-auto pb-0.5">
          {quickActions.map((action) => (
            <QuickActionTile
              key={action.id}
              icon={action.icon}
              label={action.label}
              description={action.description}
              disabled={disabled || commandBusy}
              active={selectedWorkflowId === action.id}
              onClick={() => startWorkflowChat(action.id)}
            />
          ))}
        </div>
      </div>
    ) : null;

  const workflowExecutionPanel = (
    <ActionModePanel
      ref={workflowRef}
      disabled={disabled}
      variant="command"
      hideFooterInput
      suppressInlineHints={usePlatformChat}
      onPlanResult={appendWorkflowPlanToThread}
      onWorkflowComplete={handleWorkflowComplete}
      inputValue={command}
      onInputChange={setCommand}
      onWorkflowActivityChange={handleWorkflowActivityChange}
      chatContext={context}
    />
  );

  const showWorkflowCardSlot = workflowPanelActive || workflowLoading;

  const historyToggle =
    usePlatformChat && showInsights && showConversationHistory ? (
      <button
        type="button"
        onClick={() => (historyOpen ? setHistoryOpen(false) : openHistory())}
        className={`nexus-history-toggle shrink-0 ${isLanding ? "nexus-history-toggle--inline" : ""} ${
          historyOpen ? "nexus-history-toggle--active" : ""
        }`}
        aria-label="Conversation history"
        aria-expanded={historyOpen}
      >
        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      </button>
    ) : null;

  const commandInputSection =
    !hideInput ? (
      <div
        className={`${isLanding ? "cc-landing-search-wrap" : ""}${
          isLanding && commandBarRefined ? " cc-landing-search-wrap--refined" : ""
        } ${inChatMode || isDedicatedChat ? "nexus-chat-input-bottom" : ""}`}
      >
        {isLanding ? (
          <div className="cc-landing-prompt-row flex items-center gap-2 mb-2">
            <p className="cc-landing-prompt-label mb-0">Command interface</p>
          </div>
        ) : isDedicatedChat ? (
          <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-2">
            Conversation
          </p>
        ) : null}
        {isLanding ? (
          <p className="text-xs text-slate-500 mb-2 px-1">
            <Link to={LENS_ROUTES.studioKnowledge} className="text-[#004f73] hover:underline">
              Add workspace knowledge in Studio
            </Link>{" "}
            so NEXUS can cite your organization brief and themes.
          </p>
        ) : null}
        <label htmlFor="command-center-input" className="sr-only">
          Command center
        </label>
        <div className={isLanding && historyToggle ? "cc-landing-search-row" : undefined}>
          {isLanding && historyToggle ? historyToggle : null}
          <div className={`relative ${isLanding ? "cc-landing-input-shell" : ""}`}>
          <div className={`relative ${isLanding ? "cc-landing-input-wrap" : ""}`}>
            {isLanding && commandBarRefined ? (
              <span className="cc-landing-nexus-mark" aria-hidden>
                <IconSparkles className="cc-landing-input-icon w-4 h-4 pointer-events-none" />
              </span>
            ) : (
              <IconSearch
                className={`absolute top-1/2 -translate-y-1/2 w-5 h-5 pointer-events-none ${
                  isLanding ? "left-5 text-cyan-400/50" : "left-4 text-slate-400"
                }`}
              />
            )}
            <input
              ref={commandInputRef}
              id="command-center-input"
              type="text"
              value={command}
              disabled={disabled || commandBusy || !!workflowFormId}
              onChange={(e) => setCommand(e.target.value)}
              placeholder={inputPlaceholder}
              className={
                isLanding
                  ? `cc-landing-input w-full ${
                      commandBarRefined ? "pl-[3.25rem] pr-14 py-0" : "pl-14 pr-16 py-4"
                    } text-[15px] rounded-2xl focus:outline-none disabled:opacity-50 ${
                      workflowAwaitingInput ? "cc-landing-input--active" : ""
                    }`
                  : `w-full pl-12 pr-24 py-3 text-sm text-slate-900 bg-white border rounded-2xl shadow-sm placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#004f73]/25 focus:border-[#004f73]/40 disabled:bg-slate-50 ${
                      workflowAwaitingInput
                        ? "border-[#004f73]/40 ring-2 ring-[#004f73]/10"
                        : "border-slate-200"
                    }`
              }
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  handlePrimarySubmit();
                }
              }}
            />
            <button
              type="button"
              onClick={handlePrimarySubmit}
              disabled={!canRun}
              className={
                isLanding
                  ? `cc-landing-send absolute right-2 top-1/2 -translate-y-1/2 flex items-center justify-center text-white disabled:opacity-30 transition-all ${
                      commandBarRefined
                        ? "cc-landing-send--refined w-9 h-9 rounded-[10px]"
                        : "w-10 h-10 rounded-full"
                    }`
                  : "absolute right-2 top-1/2 -translate-y-1/2 text-sm font-semibold text-white bg-[#004f73] hover:bg-[#003d5c] disabled:opacity-40 rounded-xl px-4 py-2 transition-colors"
              }
              aria-label={commandBusy ? "Working" : "Submit"}
            >
              {isLanding ? (
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 12h14M13 6l6 6-6 6" />
                </svg>
              ) : commandBusy ? (
                "…"
              ) : workflowAwaitingInput || workflowPanelActive ? (
                "Run"
              ) : (
                "Go"
              )}
            </button>
          </div>
        </div>
        </div>
      </div>
    ) : null;

  const mobileWorkflowChipsSection =
    isLanding && hideWorkflows && !inChatMode && !workflowUiActive ? (
      <div className="dashboard-mobile-workflows cc-landing-chips-wrap">
        <p className="cc-landing-chips-label">Suggested workflows</p>
        <div className="dashboard-mobile-workflows-chips">
          {quickActions.map((action) => {
            const Icon = action.icon;
            return (
              <button
                key={action.id}
                type="button"
                disabled={disabled || commandBusy}
                onClick={() => startWorkflowChat(action.id)}
                className={`cc-landing-chip cc-landing-workflow-chip shrink-0 text-xs font-medium rounded-full transition-all disabled:opacity-50 ${
                  selectedWorkflowId === action.id ? "cc-landing-chip--active" : ""
                }`}
              >
                <Icon className="dashboard-mobile-workflow-chip-icon" aria-hidden />
                {action.label}
              </button>
            );
          })}
        </div>
      </div>
    ) : null;

  const suggestedChipsSection =
    SHOW_SUGGESTED_CHIPS && showInsights && visibleChips.length && !workflowUiActive && !inChatMode ? (
      <div className={isLanding ? "cc-landing-chips-wrap" : ""}>
        {isLanding ? (
          <p className="cc-landing-chips-label">Suggested prompts</p>
        ) : (
          <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-2">
            {chipsLabel}
          </p>
        )}
        <div className={isLanding ? "cc-landing-chips-scroll" : "flex flex-wrap gap-2"}>
          <div className={isLanding ? "cc-landing-chips-row flex flex-nowrap gap-2" : "contents"}>
            {visibleChips.map((chip) => (
              <button
                key={chip.text}
                type="button"
                disabled={disabled || commandBusy}
                onClick={() => runInsight(chip.text)}
                className={
                  isLanding
                    ? `cc-landing-chip shrink-0 whitespace-nowrap text-center text-xs font-medium rounded-full px-4 py-2 transition-all disabled:opacity-50 ${
                        chip.kind === "followup" ? "cc-landing-chip--active" : ""
                      }`
                    : `text-left text-xs font-medium rounded-full px-3.5 py-1.5 transition-colors disabled:opacity-50 max-w-full ${
                        chip.kind === "followup"
                          ? "text-[#004f73] bg-[#004f73]/[0.06] border border-[#004f73]/25 hover:bg-[#004f73]/10"
                          : "text-slate-700 bg-white border border-slate-200/90 hover:border-[#004f73]/35 hover:bg-[#004f73]/[0.03]"
                      }`
                }
              >
                {chip.text}
              </button>
            ))}
          </div>
        </div>
      </div>
    ) : null;

  const threadSection =
    showInsights && hasInsightResult && showInlineConversation ? (
      <>
        {inChatMode ? (
          <p className="nexus-chat-active-label shrink-0 px-1 text-[11px] font-semibold uppercase tracking-wider">
            Active conversation
          </p>
        ) : null}
        <div className={inChatMode ? "nexus-chat-thread-wrap" : "space-y-2"}>
          {usePlatformChat ? (
            <NexusConversationThread
              messages={threadMessages}
              loading={insightLoading}
              pendingUserMessage={pendingUserMessage}
              label="NEXUS"
              onClarify={handleClarify}
              onConfirmReminders={handleConfirmReminders}
              reminderSendBusy={reminderSendBusy}
              onNavigate={(path) => navigate(path)}
              currentPath={pathname || ROUTES.dashboard}
              variant={isLanding ? "landing" : "default"}
              embedded={inChatMode || isDedicatedChat}
            />
          ) : (
            <InsightResultCard
              query={lastQuery}
              answer={answer}
              loading={insightLoading && !answer}
              label="Insight"
              relatedPages={relatedPages}
              dataSources={dataSources}
              structured={null}
            />
          )}
        </div>
      </>
    ) : null;

  const surfacesInChat =
    (inChatMode || workflowFormId) && surfaceStrip ? (
      <div className="nexus-chat-surfaces-slot nexus-chat-surfaces-slot--compact shrink-0">{surfaceStrip}</div>
    ) : null;

  const workflowFormSection = workflowFormId ? (
    <div className="nexus-workflow-form-slot">
      <p className="text-xs text-slate-500 mb-2">
        Step-by-step form — or{" "}
        <button
          type="button"
          className="text-[#004f73] font-medium hover:underline"
          onClick={() => {
            setWorkflowFormId(null);
            setPendingWorkflowId(workflowFormId);
          }}
        >
          switch back to typing in the bar above
        </button>
      </p>
      <WorkflowGuideCard
        guideId={workflowFormId}
        values={guideValues}
        onChange={updateGuideField}
        onSubmit={submitWorkflowForm}
        onSecondarySubmit={workflowFormId === "pe" ? submitPeOpenInsights : undefined}
        onCancel={cancelWorkflow}
        busy={workflowLoading}
        error={guideError}
        companyOptions={reportCompanyOptions}
        companiesLoading={companiesLoading}
        blockCatalog={blockCatalog}
        blocksCatalogLoading={blocksCatalogLoading}
      />
    </div>
  ) : null;

  const workflowBannerSection =
    showWorkflowBanner ? (
      <div className="nexus-workflow-active-banner rounded-xl border border-[#004f73]/25 bg-[#004f73]/[0.04] px-4 py-3">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-[#004f73] mb-1">
              Active workflow · {pendingGuide.title}
            </p>
            <p className="text-sm text-slate-800 leading-relaxed">{pendingGuide.askPrompt}</p>
          </div>
          <button
            type="button"
            onClick={cancelWorkflow}
            className="shrink-0 text-xs text-slate-500 hover:text-slate-800"
          >
            Cancel
          </button>
        </div>
        <button
          type="button"
          onClick={openWorkflowForm}
          className="mt-2 text-xs font-medium text-[#004f73] hover:underline"
        >
          Prefer a step-by-step form instead
        </button>
      </div>
    ) : null;

  const chatScrollContent = (
    <>
      {!isDedicatedChat && !inChatMode ? mobileWorkflowChipsSection : null}

      {!isDedicatedChat && !inChatMode ? workflowBannerSection : null}

      {isDedicatedChat || inChatMode ? workflowBannerSection : null}

      {suggestedChipsSection}

      {surfacesInChat}

      {workflowFormSection}

      {threadSection}

      <div
        className={showWorkflowCardSlot ? "nexus-workflow-execution-slot shrink-0" : "hidden"}
        aria-hidden={!showWorkflowCardSlot}
      >
        {workflowExecutionPanel}
      </div>
    </>
  );

  const chatMain = isDedicatedChat ? (
    <>
      {workflowsStrip}
      <div className="nexus-chat-scroll-pane">{chatScrollContent}</div>
      {commandInputSection}
    </>
  ) : (
    <>
      {workflowsStrip}

      {!inChatMode ? commandInputSection : null}

      {chatScrollContent}

      {inChatMode ? commandInputSection : null}
    </>
  );

  if (usePlatformChat && showInsights) {
    return (
      <div
        className={`nexus-chat-shell nexus-chat-shell--single ${
          isLanding
            ? "cc-landing nexus-chat-shell--landing"
            : isDedicatedChat
              ? "nexus-chat-shell--chat"
              : "nexus-chat-shell--dock"
        } ${inChatMode || isDedicatedChat ? "nexus-chat-shell--active" : ""}${
          showConversationHistory && historyOpen ? " nexus-chat-shell--history-open" : ""
        }`}
      >
        {showConversationHistory && historyOpen ? (
          <button
            type="button"
            className="nexus-history-backdrop"
            aria-label="Close history"
            onClick={() => setHistoryOpen(false)}
          />
        ) : null}
        {showConversationHistory ? (
          <NexusChatSidebar
            mode="drawer"
            open={historyOpen}
            onClose={() => setHistoryOpen(false)}
            sessions={historySessions}
            activeSessionId={sessionId || (threadMessages.length ? "__current__" : "")}
            loading={nexus?.conversationsLoading}
            loadingSessionId={nexus?.loadingConversationId || ""}
            onSelect={handleSelectConversation}
            onNewChat={startNewConversation}
            disabled={disabled || commandBusy}
            variant={variant}
          />
        ) : null}
        <div
          className={`nexus-chat-main flex flex-col gap-4 min-w-0 ${
            inChatMode || isDedicatedChat ? "nexus-chat-main--active" : ""
          }`}
        >
          {chatMain}
        </div>
      </div>
    );
  }

  return (
    <div className={`flex flex-col gap-4 ${isLanding ? "cc-landing" : ""}`}>{chatMain}</div>
  );
});

export default CommandCenterPanel;
